import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  DollarSign,
  Minus,
  Plus,
  Receipt,
  Calculator,
  Heart,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useWorkplaceCurrency } from '@/contexts/WorkplaceCurrencyContext';
import type { PayslipLineItem } from '@/services/payslips/types';

interface PayslipLineItemsTableProps {
  lineItems: PayslipLineItem[];
  isLoading?: boolean;
  showSensitiveData?: boolean;
}

export function PayslipLineItemsTable({
  lineItems,
  isLoading = false,
  showSensitiveData = true
}: PayslipLineItemsTableProps) {
  const { currencyCode } = useWorkplaceCurrency();
  
  // Organize line items by category
  const organizedItems = useMemo(() => {
    const categories = {
      earnings: lineItems.filter(item => item.category === 'earnings'),
      deductions: lineItems.filter(item => item.category === 'deductions'),
      taxes: lineItems.filter(item => item.category === 'taxes'),
      benefits: lineItems.filter(item => item.category === 'benefits'),
    };
    
    const totals = {
      earnings: categories.earnings.reduce((sum, item) => sum + Number(item.gross_amount), 0),
      deductions: categories.deductions.reduce((sum, item) => sum + Number(item.gross_amount), 0),
      taxes: categories.taxes.reduce((sum, item) => sum + Number(item.gross_amount), 0),
      benefits: categories.benefits.reduce((sum, item) => sum + Number(item.gross_amount), 0),
    };
    
    return { categories, totals };
  }, [lineItems]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'earnings':
        return <Plus className="h-4 w-4 text-green-600" />;
      case 'deductions':
        return <Minus className="h-4 w-4 text-red-600" />;
      case 'taxes':
        return <Receipt className="h-4 w-4 text-orange-600" />;
      case 'benefits':
        return <Heart className="h-4 w-4 text-blue-600" />;
      default:
        return <Calculator className="h-4 w-4" />;
    }
  };

  const renderLineItemSection = (items: PayslipLineItem[], category: string) => {
    if (items.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          <Calculator className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>No {category} items for this pay period</p>
        </div>
      );
    }

    return (
      <Card className="max-w-full overflow-hidden">
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-2 min-w-0">
              {getCategoryIcon(category)}
              <CardTitle className="text-base capitalize">{category}</CardTitle>
              <Badge variant="secondary" className="text-xs">
                {items.length} item{items.length !== 1 ? 's' : ''}
              </Badge>
            </div>
            <p className={cn(
              "text-lg font-bold break-all sm:text-right",
              category === 'earnings' ? "text-green-600" : "text-red-600"
            )}>
              {showSensitiveData ? formatCurrency(organizedItems.totals[category as keyof typeof organizedItems.totals]) : '****'}
            </p>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 pt-0 overflow-hidden">
          {items.map((item, index) => (
            <div key={item.id || index}>
              {index > 0 && <Separator className="mb-3" />}
              <div className="space-y-2">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm break-words">{item.description}</p>
                    {item.calculation_notes && (
                      <p className="text-xs text-muted-foreground mt-1 break-all">{item.calculation_notes}</p>
                    )}
                  </div>
                  <span className={cn(
                    "font-bold font-mono text-sm break-all sm:shrink-0",
                    category === 'earnings' ? "text-green-600" : "text-red-600"
                  )}>
                    {showSensitiveData ? formatCurrency(Number(item.gross_amount)) : '****'}
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-1 text-xs text-muted-foreground sm:grid-cols-2">
                  <span className="capitalize break-words">{item.line_item_type}</span>
                  {item.hours_applied && (
                    <span>Hours: <span className="font-mono">{Number(item.hours_applied).toFixed(1)}</span></span>
                  )}
                  {item.rate_applied && (
                    <span className="break-all">Rate: <span className="font-mono">{showSensitiveData ? formatCurrency(Number(item.rate_applied)) : '****'}</span></span>
                  )}
                  <span className="capitalize break-words">{item.calculation_method?.replace('_', ' ') || 'Standard'}</span>
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-1/4"></div>
            <div className="h-32 bg-muted rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (lineItems.length === 0) {
    return (
      <Card>
        <CardContent className="p-8">
          <div className="text-center text-muted-foreground">
            <Calculator className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-medium mb-2">No Line Items Available</h3>
            <p>
              This payslip doesn't have detailed line item breakdown.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calculate overall totals
  const grossEarnings = organizedItems.totals.earnings;
  const totalDeductions = organizedItems.totals.deductions + organizedItems.totals.taxes + organizedItems.totals.benefits;
  const netPay = grossEarnings - totalDeductions;

  return (
    <div className="space-y-6 max-w-full overflow-x-hidden">
      {/* Summary Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Pay Calculation Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex flex-col gap-2 p-3 bg-green-50 dark:bg-green-950 rounded-lg sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Gross Earnings</p>
                <p className="text-xs text-muted-foreground">
                  {organizedItems.categories.earnings.length} items
                </p>
              </div>
              <p className="text-lg font-bold text-green-600 break-all">
                {showSensitiveData ? formatCurrency(grossEarnings) : '****'}
              </p>
            </div>
            <div className="flex flex-col gap-2 p-3 bg-red-50 dark:bg-red-950 rounded-lg sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Deductions</p>
                <p className="text-xs text-muted-foreground">
                  {organizedItems.categories.deductions.length} items
                </p>
              </div>
              <p className="text-lg font-bold text-red-600 break-all">
                {showSensitiveData ? formatCurrency(organizedItems.totals.deductions) : '****'}
              </p>
            </div>
            <div className="flex flex-col gap-2 p-3 bg-orange-50 dark:bg-orange-950 rounded-lg sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Taxes</p>
                <p className="text-xs text-muted-foreground">
                  {organizedItems.categories.taxes.length} items
                </p>
              </div>
              <p className="text-lg font-bold text-orange-600 break-all">
                {showSensitiveData ? formatCurrency(organizedItems.totals.taxes) : '****'}
              </p>
            </div>
            <Separator />
            <div className="flex flex-col gap-2 p-3 bg-blue-50 dark:bg-blue-950 rounded-lg border-2 border-blue-200 dark:border-blue-800 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Net Pay</p>
                <p className="text-xs text-muted-foreground">After all deductions</p>
              </div>
              <p className="text-lg font-bold text-blue-600 break-all">
                {showSensitiveData ? formatCurrency(netPay) : '****'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Line Items by Category — stacked cards like Overview tab */}
      {organizedItems.categories.earnings.length > 0 &&
        renderLineItemSection(organizedItems.categories.earnings, 'earnings')}
      
      {organizedItems.categories.deductions.length > 0 &&
        renderLineItemSection(organizedItems.categories.deductions, 'deductions')}
      
      {organizedItems.categories.taxes.length > 0 &&
        renderLineItemSection(organizedItems.categories.taxes, 'taxes')}
      
      {organizedItems.categories.benefits.length > 0 &&
        renderLineItemSection(organizedItems.categories.benefits, 'benefits')}
    </div>
  );
}
